import config from "config";

import {
  DEFAULT_LOCAL_AI_BASE_URL,
  DEFAULT_LOCAL_API_BASE_URL,
  LOCAL_TENANTS,
  SERVICE_LOCALPORT_MAP,
} from "shared/constants";

import { getTenantFromHostname } from "./tenant-utils";

type ViaductService = "ai" | "api";

const sanitizeBaseUrl = (url: string): string => url.trim().replace(/\/+$/, "");

const isLocal = (tenant: string, instance: string): boolean =>
  tenant === "localhost" ||
  (instance === "local" && LOCAL_TENANTS.includes(tenant));

const buildLocalUrl = (service: ViaductService, tenant: string): string =>
  `${window.location.protocol}//${service}.${tenant}.cloud.viaduct.ai:${SERVICE_LOCALPORT_MAP[service]}`;

const buildLiveDeploymentUrl = (
  service: ViaductService,
  tenant: string
): string =>
  `${window.location.protocol}//${service}.${tenant}.cloud.viaduct.ai`;

const buildLocalDevelopmentUrl = (service: ViaductService): string => {
  if (service === "api") {
    return (
      import.meta.env.VITE_API_BASE_URL ??
      `${window.location.protocol}//${DEFAULT_LOCAL_API_BASE_URL}:${SERVICE_LOCALPORT_MAP.api}`
    );
  }

  return (
    import.meta.env.VITE_AI_BASE_URL ??
    `${window.location.protocol}//${DEFAULT_LOCAL_AI_BASE_URL}:${SERVICE_LOCALPORT_MAP.ai}`
  );
};

/**
 * Resolve the host base URL for a service based on the environment.
 * Handles:
 * 1. Test environment
 * 2. Local multi-tenant instance management
 * 3. Live deployments
 * 4. Local development with environment overrides
 */
const getServiceHost = (service: ViaductService): string => {
  if (import.meta.env.MODE === "test") {
    return `http://localhost:${SERVICE_LOCALPORT_MAP[service]}`;
  }

  const tenant = getTenantFromHostname();
  const conf = config.get();

  /*
   * If conf.apiBaseURL or aiBaseUrl is set use that in favor of the generated one.
   */
  if (service === "api" && conf.apiBaseUrl)
    return sanitizeBaseUrl(conf.apiBaseUrl);

  if (service === "ai" && conf.aiBaseUrl)
    return sanitizeBaseUrl(conf.aiBaseUrl);

  const local = isLocal(tenant, conf.instance);

  if (config.isInstanceManagement() && local) {
    return buildLocalUrl(service, tenant);
  } else if (local) {
    return buildLocalDevelopmentUrl(service);
  }

  return buildLiveDeploymentUrl(service, tenant);
};

/**
 * Get the service base URL with its path prefix (`/api` or `/ai`) appended.
 */
export const getAPIUrl = (service: ViaductService): string =>
  `${getServiceHost(service)}/${service}`;
